import { useState, useEffect, useCallback } from "react";
import { useClientDesignationApi } from "../../../hooks/useClientDesignationApi";

export const useClientDesignationsViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const {
    getClientDesignations,
    createClientDesignation: apiCreateClientDesignation,
    updateClientDesignation: apiUpdateClientDesignation,
    deleteClientDesignation,
    cacheOperations: { readClientDesignations, writeClientDesignations },
  } = useClientDesignationApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [clientDesignationsResult] = await Promise.all([
        getClientDesignations(),
      ]);

      if (!clientDesignationsResult.success) {
        throw new Error("Failed to fetch required data");
      }

      writeClientDesignations(clientDesignationsResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [getClientDesignations, writeClientDesignations, loading]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateClientDesignation = useCallback(
    async (formData) => {
      try {
        const result = await apiCreateClientDesignation(formData);
        if (result.success) {
          setSuccessModal("Client Designation created successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error creating client designation:", error);
        throw new Error(error.message);
      }
    },
    [
      apiCreateClientDesignation,
      readClientDesignations,
      writeClientDesignations,
    ]
  );

  const handleUpdateClientDesignation = useCallback(
    async (clientDesignationId, formData) => {
      try {
        const result = await apiUpdateClientDesignation(
          clientDesignationId,
          formData
        );
        if (result.success) {
          setSuccessModal("Client designation updated successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error updating client designation:", error);
        throw new Error(error.message);
      }
    },
    [
      apiUpdateClientDesignation,
      readClientDesignations,
      writeClientDesignations,
    ]
  );

  const handleDelete = useCallback(
    async (clientDesignation) => {
      try {
        const result = await deleteClientDesignation(
          clientDesignation.client_designation_id
        );
        if (result.success) {
          setSuccessModal("Client designation deleted successfully");
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting client designation:", err);
      }
    },
    [deleteClientDesignation, readClientDesignations, writeClientDesignations]
  );

  return {
    clientDesignations: readClientDesignations(),
    loading,
    error,
    createClientDesignation: handleCreateClientDesignation,
    updateClientDesignation: handleUpdateClientDesignation,
    handleDelete,
    successModal,
    setSuccessModal,
    refetch: fetchData,
  };
};
